import React, { useState } from "react"
import { manageMedicalRecordDelete } from "../../../../api/manage_medical_record_delete"
import { ManageDeviceData } from "../../device/"
import { MedicalRecordInfo } from "../../../tenant/components/TenantInfo/"
import { useNavigate } from "react-router-dom"
import { useLoading } from "../../../LoadingContext"

import Card from "@mui/material/Card"
import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDInput from "components/MDInput"
import MDButton from "components/MDButton"

interface ManageMedicalRecordDeleteProps {
  facilityId: string
  deviceData: ManageDeviceData[]
  deviceDataNow: ManageDeviceData[]
  devicePlace: string[]
  onClose: (num: number) => void // モーダルを閉じるためのコールバック
  initialData: MedicalRecordInfo // 既存のデータを引数として受け取る
}

const ManageMedicalRecordDelete: React.FC<ManageMedicalRecordDeleteProps> = ({
  deviceData,
  deviceDataNow,
  devicePlace,
  onClose,
  initialData
}) => {
  const { startLoading, stopLoading } = useLoading()
  const navigate = useNavigate()
  const deviceIds: string[] = deviceDataNow.map((device) => device.id)
  const [formData] = useState<{
    id: string
    room_no: string
    name: string
    initial: string
    age: string
    birthday: string
    phone: string
    devices: string[] // 型をstring[]に指定
    devicePlaces: string[] // 型をstring[]に指定
  }>({
    id: initialData.id?.toString() || "",
    room_no: initialData.room_no?.toString() || "",
    name: initialData.name || "",
    initial: initialData.initial || "",
    age: initialData.age?.toString() || "",
    birthday: initialData.birthday?.toString() || "",
    phone: initialData.phone || "",
    devices: deviceIds, // 初期値は空の配列
    devicePlaces: initialData.device_places || [] // 初期値は空の配列
  })

  const [errors, setErrors] = useState({
    server: ""
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const validationErrors = { ...errors }
    validationErrors.server = ""
    setErrors(validationErrors)

    try {
      startLoading()
      await manageMedicalRecordDelete(initialData.id?.toString() || "")
      onClose(1)
    } catch (error: any) {
      if (error.response && error.response.status === 401) {
        navigate("/login") // セッション切れ時にトップページへリダイレクト
      } else if (error.response) {
        const errorDetail = error.response.data.detail
        const validationErrors = { ...errors }
        validationErrors.server = errorDetail.message
        setErrors(validationErrors)
      } else {
        const validationErrors = { ...errors }
        validationErrors.server = "サーバーへの接続に失敗しました"
        setErrors(validationErrors)
      }
    } finally {
      stopLoading()
    }
  }

  // すでに選択されたデバイスを除外しつつ、選択されたデバイスを選択肢に保持する
  const getAvailableDevices = (index: number) => {
    // 他の選択されたデバイスを除外して新しいリストを生成
    const selectedDevices = [...formData.devices]
    selectedDevices.splice(index, 1) // 現在選択されているデバイスを除外
    return deviceData.filter((device) => !selectedDevices.includes(device.id))
  }

  // すでに選択された設置場所を除外しつつ、選択された設置場所を選択肢に保持する
  const getAvailableDevicePlaces = (index: number) => {
    // 他の選択された設置場所を除外して新しいリストを生成
    const selectedPlaces = [...formData.devicePlaces]
    selectedPlaces.splice(index, 1) // 現在選択されている設置場所を除外
    return devicePlace.filter((place) => !selectedPlaces.includes(place))
  }

  return (
    <div className="modal-bg-cover">
      <Card
        sx={{
          width: "90%",
          maxWidth: "500px",
          maxHeight: "calc(100vh - 100px)",
          marginTop: "50px",
          marginBottom: "50px",
          paddingTop: "20px",
          paddingBottom: "20px",
          paddingLeft: "20px"
        }}
      >
        <button className="close-button" onClick={() => onClose(0)}>
          ×
        </button>
        <MDTypography variant="h4" fontWeight="medium">
          入居者を削除
        </MDTypography>
        <MDBox style={{ height: "20px" }}></MDBox>
        <MDBox
          style={{
            maxHeight: "calc(100vh - 200px)",
            overflowY: "auto",
            paddingTop: "10px",
            paddingLeft: "5px",
            paddingRight: "25px"
          }}
        >
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <MDInput
              label="居室ID"
              type="text"
              name="id"
              value={formData.id}
              inputProps={{ readOnly: true }}
              sx={{ width: "100%" }}
            />
          </MDBox>
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <MDInput
              label="居室番号"
              type="text"
              name="room_no"
              value={formData.room_no}
              inputProps={{ readOnly: true }}
              sx={{ width: "100%" }}
            />
          </MDBox>
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <MDInput
              label="氏名"
              type="text"
              name="name"
              value={formData.name}
              inputProps={{ readOnly: true }}
              sx={{ width: "100%" }}
            />
          </MDBox>
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <MDInput
              label="イニシャル"
              type="text"
              name="initial"
              value={formData.initial}
              inputProps={{ readOnly: true }}
              sx={{ width: "100%" }}
            />
          </MDBox>
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <MDInput
              label="生年月（YYYYMM）"
              type="text"
              name="birthday"
              value={formData.birthday}
              inputProps={{ readOnly: true }}
              sx={{ width: "100%" }}
            />
          </MDBox>
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <MDInput
              label="電話番号"
              type="text"
              name="phone"
              value={formData.phone}
              inputProps={{ readOnly: true }}
              sx={{ width: "100%" }}
            />
          </MDBox>
          <MDTypography display="flex" alignItems="center" mb={1} sx={{ fontSize: "0.75em" }}>
            デバイス
          </MDTypography>
          {formData.devices.map((device, index) => (
            <MDBox
              key={"device" + index}
              display="flex"
              gap={3}
              flexWrap="wrap"
              justifyContent="flex-end"
              sx={{ fontSize: "0.875rem", color: "text.primary", textAlign: "right", width: "100%" }}
            >
              <select name={`device_${index + 1}`} value={device} disabled>
                <option value="">選択してください</option>
                {getAvailableDevices(index).map((deviceItem) => (
                  <option key={deviceItem.id} value={deviceItem.id}>
                    {deviceItem.serial_no}
                  </option>
                ))}
              </select>
            </MDBox>
          ))}
          <MDTypography display="flex" alignItems="center" mt={1} mb={1} sx={{ fontSize: "0.75em" }}>
            設置場所
          </MDTypography>
          {formData.devicePlaces.map((place, index) => (
            <MDBox
              key={"device" + index}
              display="flex"
              gap={3}
              flexWrap="wrap"
              justifyContent="flex-end"
              sx={{ fontSize: "0.875rem", color: "text.primary", textAlign: "right", width: "100%" }}
            >
              <select name={`devicePlace_${index + 1}`} value={place} disabled>
                <option value="">設置場所を選択</option>
                {getAvailableDevicePlaces(index).map((placeItem, i) => (
                  <option key={i} value={placeItem}>
                    {placeItem}
                  </option>
                ))}
              </select>
            </MDBox>
          ))}
          {errors.server && (
            <MDBox
              color="#FF0000" // テキスト色
              pl={2} // 左パディング
              mb={2} // 下マージン
              fontSize="0.7em" // フォントサイズ
            >
              {errors.server}
            </MDBox>
          )}
          <MDBox mt={4} mb={2}>
            <MDButton
              variant="contained"
              color="info"
              fullWidth
              sx={{
                borderRadius: "4px",
                textTransform: "none",
                fontSize: "1rem",
                padding: "0.75rem 0",
                backgroundColor: "#1976d2",
                "&:hover": {
                  backgroundColor: "#1565c0"
                }
              }}
              onClick={(e) => {
                handleSubmit(e)
              }}
            >
              削除
            </MDButton>
          </MDBox>
        </MDBox>
      </Card>
    </div>
  )
}

export default ManageMedicalRecordDelete
